'use client';
import { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faXmark } from '@fortawesome/pro-solid-svg-icons';
import log from '@/utils/logger';

interface SearchInputBasicProps {
  onSearch: (query: string) => Promise<{ id: string; title: string; slug: string }[]>;
  onResultClick: (slug: string) => void;
  placeholder?: string;
}

const SearchInputBasic: React.FC<SearchInputBasicProps> = ({ onSearch, onResultClick, placeholder = 'Suche...' }) => {
  const [inputText, setInputText] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<{ id: string; title: string; slug: string }[]>([]);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(event.target.value);
  };

  const handleSearch = useCallback(async () => {
    if (!inputText.trim()) return;

    setIsSearchLoading(true);
    try {
      const results = await onSearch(inputText.trim());
      setSearchResults(results.slice(0, 5));
      setHasSearched(true);
    } catch (error) {
      log.error('Error during search: ', error);
      setHasSearched(true);
    } finally {
      setIsSearchLoading(false);
    }
  }, [inputText, onSearch]);

  const closeDropdown = () => {
    setInputText('');
    setSearchResults([]);
    setHasSearched(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      closeDropdown();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  useEffect(() => {
    if (inputText.trim()) {
      handleSearch();
    } else {
      closeDropdown();
    }
  }, [handleSearch, inputText]);

  return (
    <div className="relative w-full max-w-sm" ref={dropdownRef}>
      <div className="input input-bordered flex w-full items-center justify-between text-sm text-primary md:min-w-sm">
        <div className="flex grow">
          {isSearchLoading ? (
            <span className="loading loading-spinner h-4 w-4" />
          ) : (
            <FontAwesomeIcon
              icon={faSearch}
              className={`h-4 w-4 text-primary ${!inputText.trim() ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              onClick={handleSearch}
            />
          )}
          <input
            type="text"
            className="grow overflow-ellipsis px-2 max-xs:max-w-30 max-xxs:max-w-24"
            placeholder={placeholder}
            value={inputText}
            onChange={handleInputChange}
          />
        </div>
        {hasSearched && (
          <button onClick={closeDropdown}>
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4 cursor-pointer text-primary" />
          </button>
        )}
      </div>
      {hasSearched && (
        <div className="absolute z-10 mt-1 w-full border-separate rounded-xl border bg-base-100 shadow-lg">
          {searchResults.length > 0 ? (
            searchResults.map((result) => (
              <div
                key={result.id}
                className="cursor-pointer px-4 py-2 hover:underline"
                onClick={() => onResultClick(result.slug)}
              >
                {result.title}
              </div>
            ))
          ) : (
            <div className="px-4 py-2">Keine passenden Artikel gefunden</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchInputBasic;
